import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createAction, createFeatureSelector, createReducer, createSelector, on, props } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { SumarioPublicacaoHtmlOutputDto } from 'src/app/domain/DTOs/output/publicacao/sumario-publicacao-html-output.dto';
import { EdicaoPublicadaService } from 'src/app/services/edicao-publicada.service';
import { CommonMessages } from 'src/app/utils/common.messages';

// -- Actions
const actionName = '[Obter Sumario Publicacao HTML]';

export const obterSumarioPublicacaoHtml = createAction(`${actionName} Obter`, props<{ edicaoId: number }>());
export const obterSumarioPublicacaoHtmlSuccess = createAction(
  `${actionName} Sucesso`,
  props<{ edicaoId: number; dados: SumarioPublicacaoHtmlOutputDto }>()
);
export const obterSumarioPublicacaoHtmlFailure = createAction(
  `${actionName} Falha`,
  props<{ edicaoId: number; erro: string }>()
);
export const obterSumarioPublicacaoHtmlFinish = createAction(`${actionName} Concluir`);

export interface ObterSumarioPublicacaoHtmlState {
  dadosPorEdicao: Record<number, SumarioPublicacaoHtmlOutputDto>;
  errosPorEdicao: Record<number, string>;
  edicoesCarregando: number[];
}

const initialState: ObterSumarioPublicacaoHtmlState = {
  dadosPorEdicao: {},
  errosPorEdicao: {},
  edicoesCarregando: []
};

// -- Reducer
export const obterSumarioPublicacaoHtmlReducer = createReducer(
  initialState,
  on(obterSumarioPublicacaoHtml, (state, { edicaoId }) => {
    const errosPorEdicao = { ...state.errosPorEdicao };
    delete errosPorEdicao[edicaoId];
    return {
      ...state,
      errosPorEdicao,
      edicoesCarregando: state.edicoesCarregando.includes(edicaoId)
        ? state.edicoesCarregando
        : [...state.edicoesCarregando, edicaoId]
    };
  }),
  on(obterSumarioPublicacaoHtmlSuccess, (state, { edicaoId, dados }) => ({
    ...state,
    dadosPorEdicao: { ...state.dadosPorEdicao, [edicaoId]: dados },
    edicoesCarregando: state.edicoesCarregando.filter(id => id !== edicaoId)
  })),
  on(obterSumarioPublicacaoHtmlFailure, (state, { edicaoId, erro }) => ({
    ...state,
    errosPorEdicao: { ...state.errosPorEdicao, [edicaoId]: erro },
    edicoesCarregando: state.edicoesCarregando.filter(id => id !== edicaoId)
  })),
  on(obterSumarioPublicacaoHtmlFinish, () => initialState)
);

// -- Selector
export const selectObterSumarioPublicacaoHtmlState =
  createFeatureSelector<ObterSumarioPublicacaoHtmlState>('obterSumarioPublicacaoHtml');
export const selectDadosObterSumarioPublicacaoHtml =
  createSelector(selectObterSumarioPublicacaoHtmlState, state => state.dadosPorEdicao);
export const selectCarregandoObterSumarioPublicacaoHtml =
  createSelector(selectObterSumarioPublicacaoHtmlState, state => state.edicoesCarregando.length > 0);

// -- Effect
@Injectable()
export class ObterSumarioPublicacaoHtmlEffects {
  obterSumario$ = createEffect(() => this.actions$.pipe(
    ofType(obterSumarioPublicacaoHtml),
    mergeMap(({ edicaoId }) => this.edicaoPublicadaService.obterSumario(edicaoId).pipe(
      map(response => response.erros?.length
        ? obterSumarioPublicacaoHtmlFailure({ edicaoId, erro: response.erros[0] })
        : obterSumarioPublicacaoHtmlSuccess({ edicaoId, dados: response.dados })),
      catchError(() => of(obterSumarioPublicacaoHtmlFailure({
        edicaoId,
        erro: CommonMessages.EdicaoHtml.CarregarMaterias
      }))),
      takeUntil(this.actions$.pipe(ofType(obterSumarioPublicacaoHtmlFinish)))
    ))
  ));

  constructor(private readonly actions$: Actions, private readonly edicaoPublicadaService: EdicaoPublicadaService) {}
}
